import React, { useEffect, useState } from "react";
import { Users, Trash2, Loader2, ShieldCheck, PencilLine, KeyRound, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function UsersDialog({ open, onClose, me }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "user" });
  const [busy, setBusy] = useState(false);
  const [pwFor, setPwFor] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [resetText, setResetText] = useState("");

  const resetAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset-data", { confirm: resetText });
      toast.success(data.message); setResetText(""); onClose();
      setTimeout(() => window.location.reload(), 800);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setPw = async () => {
    if (newPw.length < 6) { toast.error("Şifre en az 6 karakter olmalı"); return; }
    setBusy(true);
    try { await api.put(`/users/${pwFor.id}/password`, { new_password: newPw }); toast.success("Şifre güncellendi"); setPwFor(null); setNewPw(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { if (open) load(); }, [open]);

  const add = async () => {
    if (!form.email || form.password.length < 6) { toast.error("E-posta ve en az 6 karakter şifre gerekli"); return; }
    setBusy(true);
    try { await api.post("/auth/register", form); toast.success("Kullanıcı eklendi"); setForm({ name: "", email: "", password: "", role: "user" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    try { await api.delete(`/users/${id}`); toast.success("Kullanıcı silindi"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="users-dialog">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Users className="h-5 w-5 text-primary" /> Kullanıcı Yönetimi</DialogTitle></DialogHeader>
        <div className="divide-y divide-border/60 rounded-lg border border-border">
          {users.map((u) => (
            <div key={u.id} data-testid={`user-row-${u.id}`} className="flex items-center gap-3 px-3 py-2 text-sm">
              {u.role === "admin" ? <ShieldCheck className="h-4 w-4 text-primary" /> : <PencilLine className="h-4 w-4 text-sky-400" />}
              <div className="flex-1 min-w-0"><div className="font-medium truncate">{u.name || "—"} <span className="text-xs text-muted-foreground">· {u.email}</span></div>
                <div className="text-[11px] text-muted-foreground">{u.role === "admin" ? "Yönetici — tüm veriler" : "Veri Giriş — maliyet & kâr/zarar gizli"}</div></div>
              {u.id !== me?.id && <>
                <button onClick={() => setPwFor(u)} data-testid={`user-setpw-${u.id}`} title="Şifre ata" className="h-7 w-7 rounded-md hover:bg-accent inline-flex items-center justify-center text-muted-foreground hover:text-primary"><KeyRound className="h-3.5 w-3.5" /></button>
                <button onClick={() => del(u.id)} data-testid={`user-delete-${u.id}`} className="h-7 w-7 rounded-md hover:bg-destructive/10 inline-flex items-center justify-center text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
              </>}
            </div>
          ))}
        </div>
        {pwFor && (
          <div className="rounded-lg border border-primary/40 bg-primary/5 p-3 space-y-2" data-testid="user-setpw-panel">
            <div className="text-xs font-medium">{pwFor.email} için yeni şifre</div>
            <div className="flex gap-2">
              <Input type="password" placeholder="Yeni şifre (min 6)" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="user-setpw-input" />
              <Button onClick={setPw} disabled={busy} data-testid="user-setpw-save">Kaydet</Button>
              <Button variant="outline" onClick={() => { setPwFor(null); setNewPw(""); }}>Vazgeç</Button>
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Ad Soyad" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-form-name" />
          <Input placeholder="E-posta" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-form-email" />
          <Input placeholder="Şifre (min 6)" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-form-password" />
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="user">Veri Giriş Kullanıcısı</SelectItem><SelectItem value="admin">Yönetici</SelectItem></SelectContent>
          </Select>
        </div>
        <Button onClick={add} disabled={busy} data-testid="user-form-save" className="gap-2 w-full">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Kullanıcı Ekle</Button>

        <div className="rounded-lg border border-rose-500/40 bg-rose-500/5 p-3 space-y-2" data-testid="reset-data-panel">
          <div className="flex items-center gap-2 text-sm font-semibold text-rose-400"><AlertTriangle className="h-4 w-4" /> Tüm Verileri Sıfırla</div>
          <p className="text-xs text-muted-foreground">Müşteri, günlük giriş, cari, maliyet, personel, tedarikçi ve tüm hareketler silinir. Kullanıcı hesapları korunur. Geri alınamaz. Onaylamak için <b>SIFIRLA</b> yazın.</p>
          <div className="flex gap-2">
            <Input value={resetText} onChange={(e) => setResetText(e.target.value)} placeholder="SIFIRLA" data-testid="reset-confirm-input" className="font-mono" />
            <Button variant="destructive" disabled={resetText !== "SIFIRLA" || busy} onClick={resetAll} data-testid="reset-data-btn">Sıfırla</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
